import clsx from "~/sdk/clx"
import { useSignal, useSignalEffect } from "~/sdk/signal"
import Image from "~/components/ui/Image"
import Icon from "~/components/ui/Icon"
import Modal from "~/components/ui/Modal"
import { ComponentProps, MouseEvent as ReactMouseEvent } from "react"
import { useId, useRef } from "react"

interface ContainerBounds {
    x: number
    y: number
    width: number
    height: number
}

const clamp = (min: number, max: number, value: number) => Math.min(Math.max(value, min), max)

const getBounds = (element?: Element | null): ContainerBounds | null => {
    if (!element) return null

    const bounds = element.getBoundingClientRect()

    return {
        x: bounds.left,
        y: bounds.top,
        width: bounds.width,
        height: bounds.height,
    }
}

const getMousePositionFromEvent = (
    event: MouseEvent,
    bounds?: ContainerBounds | null,
): { x: number; y: number; isOutOfBounds?: boolean } => {
    if (!bounds) {
        return { x: 0, y: 0 }
    }

    const [x, y] = [event.clientX - bounds.x, event.clientY - bounds.y]

    const threshold = 20
    const isOutOfBounds = x < -threshold || y < -threshold || x > bounds.width + threshold ||
        y > bounds.height + threshold

    return {
        x: clamp(0, bounds.width, x),
        y: clamp(0, bounds.height, y),
        isOutOfBounds,
    }
}

function ZoomableImage(props: ComponentProps<typeof Image>) {
    const isZoomedIn = useSignal(false)
    const id = useId()

    const lensRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLLabelElement>(null)
    const resultRef = useRef<HTMLDivElement>(null)
    const imageRef = useRef<HTMLImageElement>(null)

    const containerBounds = useRef<ContainerBounds | null>(null)

    // Took from https://www.w3schools.com/howto/howto_js_image_zoom.asp
    const setPositionAndScale = (mouseX: number, mouseY: number) => {
        const { width = 0, height = 0 } = getContainerBounds() ?? {}

        // subtract 100px to center the lens
        // clamp to prevent the lens from going out of bounds
        const lensLeft = clamp(0, width - 200, mouseX - 100)
        const lensTop = clamp(0, height - 200, mouseY - 100)

        if (lensRef.current) {
            lensRef.current.style.left = `${lensLeft}px`
            lensRef.current.style.top = `${lensTop}px`
        }

        if (resultRef.current && lensRef.current && imageRef.current) {
            // calculate the ratio between result DIV and lens
            const cx = resultRef.current.offsetWidth / lensRef.current.offsetWidth
            const cy = resultRef.current.offsetHeight / lensRef.current.offsetHeight

            const imageWidth = imageRef.current.width
            const imageHeight = imageRef.current.height
            const backgroundSize = `${cx * imageWidth}px ${cy * imageHeight}px`
            const backgroundPosition = `-${lensLeft * cx}px -${lensTop * cy}px`

            resultRef.current.style.backgroundSize = backgroundSize
            resultRef.current.style.backgroundPosition = backgroundPosition
        }
    }

    const getContainerBounds = () => getBounds(containerRef.current)

    const handleMouseOver = () => {
        isZoomedIn.value = true
    }

    useSignalEffect(() => {
        if (!isZoomedIn.value) {
            setPositionAndScale(0, 0)
            containerBounds.current = null
        }
    })

    // Local rAF-throttled handler bound to the container's onMouseMove
    // (was previously `document.addEventListener("mousemove")` on every
    // ZoomableImage mount, which meant N global listeners reflowing on
    // every mouse movement on the page — the PDP has 5+ images so this
    // froze the tab on Chrome's "page unresponsive" timeout).
    const rafIdRef = useRef<number | null>(null)
    const handleMouseMove = (event: ReactMouseEvent<HTMLLabelElement>) => {
        if (!isZoomedIn.value) {
            return
        }

        const clientX = event.clientX
        const clientY = event.clientY

        if (rafIdRef.current !== null) {
            return
        }
        rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = null
            if (!isZoomedIn.value) return
            const bounds = getContainerBounds()
            const mousePosition = getMousePositionFromEvent(
                { clientX, clientY } as MouseEvent,
                bounds,
            )
            if (mousePosition.isOutOfBounds) {
                isZoomedIn.value = false
            } else {
                setPositionAndScale(mousePosition.x, mousePosition.y)
            }
        })
    }

    return (
        <>
            <Modal id={id}>
                <div className='absolute border-4 border-[#595955] top-[5%]'>
                    <Image
                        src={props.src}
                        alt={props.alt}
                        width={550}
                        height={550}
                        loading={props.loading}
                        fit='contain'
                    />

                    <label
                        htmlFor={id}
                        className='absolute top-0 right-0 p-3 cursor-pointer'
                    >
                        <Icon id='PDPX' size={16} className='text-black' strokeWidth={1} role='img' aria-label='Fechar imagem' />
                    </label>
                </div>
            </Modal>
            <label
                htmlFor={id}
                ref={containerRef}
                onMouseOver={handleMouseOver}
                onMouseMove={handleMouseMove}
                onMouseLeave={() => {
                    isZoomedIn.value = false
                }}
                className='relative overflow-hidden cursor-zoom-in'
            >
                <div
                    ref={lensRef}
                    className={clsx(
                        isZoomedIn.value ? 'visible' : 'invisible',
                        'w-[200px] h-[200px] border border-neutral-400 bg-white/40 absolute z-10',
                    )}
                />
                <div
                    className={clsx(
                        isZoomedIn.value ? 'visible' : 'invisible',
                        'absolute top-0 left-0 w-[400px] h-[400px] overflow-hidden bg-white border-4 border-neutral-400 z-10',
                    )}
                >
                    <div
                        ref={resultRef}
                        style={{
                            backgroundImage: `url('${props.src}')`,
                            backgroundRepeat: 'no-repeat',
                        }}
                        className='absolute w-full h-full'
                    />
                </div>
                <Image ref={imageRef} {...props} fit='contain' />
            </label>
        </>
    )
}

export default ZoomableImage
